// src/components/shared/CustomYouTubePlayer.jsx

import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle2, RefreshCw, ChevronRight } from 'lucide-react';

const CustomYouTubePlayer = ({ videoId, onNext }) => {
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const [isEnded, setIsEnded] = useState(false);

    useEffect(() => {
        setIsEnded(false); 
        if (!videoId) return;

        const loadAPI = () => {
            return new Promise((resolve) => {
                if (window.YT && window.YT.Player) {
                    resolve(window.YT);
                    return;
                }
                
                if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                    const tag = document.createElement('script');
                    tag.src = 'https://www.youtube.com/iframe_api';
                    const firstScriptTag = document.getElementsByTagName('script')[0];
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                }
                
                window.onYouTubeIframeAPIReady = () => resolve(window.YT);
            });
        };

        let player;
        loadAPI().then((YT) => {
            if (!containerRef.current) return;
            
            const el = document.createElement('div');
            el.className = "w-full h-full absolute inset-0";
            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(el);

            player = new YT.Player(el, {
                videoId: videoId,
                playerVars: { 
                    rel: 0, 
                    modestbranding: 1, 
                    playsinline: 1,
                    showinfo: 0,
                    autoplay: 1 // Auto-play support
                },
                events: {
                    onStateChange: (event) => {
                        if (event.data === YT.PlayerState.ENDED) {
                            // FORCE EXIT FULLSCREEN ON END
                            if (document.fullscreenElement) {
                                document.exitFullscreen().catch(() => {});
                            } else if (document.webkitFullscreenElement) {
                                document.webkitExitFullscreen().catch(() => {});
                            } else if (document.mozFullScreenElement) {
                                document.mozCancelFullScreen().catch(() => {});
                            } else if (document.msFullscreenElement) {
                                document.msExitFullscreen().catch(() => {});
                            }

                            setIsEnded(true);
                        } else if (event.data === YT.PlayerState.PLAYING) {
                            setIsEnded(false);
                        }
                    }
                }
            });
            playerRef.current = player;
        });

        return () => {
            if (playerRef.current && playerRef.current.destroy) {
                playerRef.current.destroy();
            }
        };
    }, [videoId]);

    return (
        <div className="relative w-full h-full bg-black rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10">
            <div ref={containerRef} className="absolute inset-0 w-full h-full"></div>
            
            {isEnded && (
                <div className="absolute inset-0 z-50 bg-slate-900 flex flex-col items-center justify-center animate-in fade-in duration-300">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-500/10 rounded-full flex items-center justify-center mb-6 border border-blue-500/20 shadow-[0_0_30px_rgba(59,130,246,0.2)]">
                        <CheckCircle2 size={40} className="text-brand-primary" />
                    </div>
                    <h3 className="text-white text-2xl md:text-3xl font-black mb-8 text-center px-4 tracking-tight">Complete</h3>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                        <button 
                            onClick={() => {
                                setIsEnded(false);
                                if (playerRef.current) playerRef.current.playVideo();
                            }} 
                            className="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2 border border-white/10"
                        >
                            <RefreshCw size={18} /> Replay
                        </button>
                        
                        {onNext && (
                            <button 
                                onClick={onNext}
                                className="px-8 py-3.5 bg-brand-primary hover:bg-blue-600 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-blue-500/30 hover:scale-105"
                            >
                                Next <ChevronRight size={20} />
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomYouTubePlayer;